import Button, {ButtonVariant} from 'bitmovin-web-ui/src/components/core/button';
import React from 'react';
import {connect} from 'react-redux';
import {Route, Switch} from 'react-router';
import styled from 'styled-components';

import {AppRoutePathFunction, AppRouteToPath} from '../../logic/routes';
import {RootState} from '../../logic/store';
import * as AuthenticationStore from '../../logic/store/authenticationStore';
import {AuthenticationStatus} from '../../logic/store/authenticationStore';
import {ThunkDispatch} from '../../logic/store/redux-thunk';
import BitmovinLogoSvgPath from '../../media/bitmovin_logo.svg';
import {Link, maxExtendedWidth, pxToEm} from '../globalStyles';
import QuickSwitch from '../layout/quickSwitch';

//#region Styled Components

const StyledHeader = styled.header`
  display: flex;
  justify-content: center;
  box-shadow: 0 0 4px rgba(180, 204, 219, 0.43);
`;

const spaceBetweenButtonsPx = 10;
const StyledHeaderContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  max-width: ${maxExtendedWidth};
  padding: ${pxToEm(spaceBetweenButtonsPx)}em ${pxToEm(spaceBetweenButtonsPx)}em;
`;

const StyledActionsDiv = styled.div`
  > :not(:last-child) {
    margin-right: ${pxToEm(spaceBetweenButtonsPx)}em;
  }
`;

const buttonMinWidthPx = 150;
const StyledButton = styled(Button)`
  min-width: ${pxToEm(buttonMinWidthPx)}em !important;
`;

const StyledQuickSwitch = styled(QuickSwitch)`
  min-width: ${pxToEm(buttonMinWidthPx)}em !important;
`;

const StyledLogoutButton = styled(StyledButton)`
  margin-left: ${pxToEm(spaceBetweenButtonsPx)}em;
`;

//#endregion

interface MapStateToProps {
  authenticationState?: AuthenticationStore.State;
}

interface MapDispatchToProps {
  logout?: () => void;
}

interface Props extends MapStateToProps, MapDispatchToProps {}

const Header: React.FunctionComponent<Props> = ({authenticationState, logout}) => {
  function renderLogo() {
    return (
      <Switch>
        <Route path={AppRouteToPath.Root} exact={true} component={() => <div />} />
        <Route
          path={AppRouteToPath.Root}
          component={() => (
            <Link to={AppRoutePathFunction.Root()}>
              <img src={BitmovinLogoSvgPath} alt={'Bitmovin logo'} />
            </Link>
          )}
        />
      </Switch>
    );
  }

  function renderLogoutButton() {
    if (authenticationState.status === AuthenticationStatus.Offline) {
      return null;
    }

    return (
      <StyledLogoutButton
        variant={ButtonVariant.OutlinedLight}
        disabled={authenticationState.status === AuthenticationStatus.LoggedOut}
        onClick={() => logout()}>
        Logout
      </StyledLogoutButton>
    );
  }

  return (
    <StyledHeader>
      <StyledHeaderContent>
        {renderLogo()}

        <StyledActionsDiv>
          <a href="https://www.bitmovin.com/" target="_blank">
            <StyledButton variant={ButtonVariant.OutlinedLight}>Website</StyledButton>
          </a>
          <a href="https://www.bitmovin.com/docs" target="_blank">
            <StyledButton variant={ButtonVariant.OutlinedLight}>Documentation</StyledButton>
          </a>
          <Link to={AppRoutePathFunction.AboutUs()}>
            <StyledButton variant={ButtonVariant.OutlinedLight}>About Us</StyledButton>
          </Link>
          <StyledQuickSwitch />
          {renderLogoutButton()}
        </StyledActionsDiv>
      </StyledHeaderContent>
    </StyledHeader>
  );
};

const mapStateToProps = (state: RootState): MapStateToProps => ({
  authenticationState: state.authentication
});

const mapDispatchToProps = (dispatch: ThunkDispatch): MapDispatchToProps => {
  return {
    logout: async () => {
      await dispatch(AuthenticationStore.logout());
    }
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(Header);
